import { useId, useState, type ChangeEvent, type FormEvent } from 'react';
import { Button, Label, Popover } from '@scholastic/mockingjay';
import {
  ExclamationTriangleIcon,
  InfoCircledIcon,
} from '@radix-ui/react-icons';
import { useGetProductByAccessCodeApi } from 'eds-common/api/v2';
import { cn } from 'eds-common';
import { useAccessCodeRedemptionContext } from './AccessCodeRedemptionContext';

const ACCESS_CODE_HELP_TRIGGER_LABEL = 'About access codes';
const ACCESS_CODE_LABEL = 'Access code';
const MAGAZINES_SUPPORT_EMAIL = 'magazinecs@scholastic.com';

function accessCodeErrorMessage(errorCode: string | undefined): string {
  if (errorCode === 'access.code.expired') {
    return 'The access code you entered has expired. Check your code and try again.';
  }
  if (errorCode === 'start.date.after.current.date') {
    return 'The access code you entered is not yet active. Check your code and try again.';
  }
  return 'The access code you entered is incorrect. Check your code and try again.';
}

export function RedeemAccessCode() {
  const { goToNextStep, updateRedemptionData } =
    useAccessCodeRedemptionContext();
  const [inputAccessCode, setInputAccessCode] = useState('');
  const inputId = useId();
  const errorId = `${inputId}-error`;

  const { isRefetching, isError, error, refetch } =
    useGetProductByAccessCodeApi(inputAccessCode, {
      enabled: false,
      retry: false,
    });

  const errorCode = isError ? error?.cause.errorBody?.code : undefined;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    async function handleAsyncSubmit() {
      e.preventDefault();

      const result = await refetch();

      if (result.data) {
        updateRedemptionData({
          product: result.data,
          accessCode: inputAccessCode,
        });
        goToNextStep();
      }
    }

    handleAsyncSubmit().catch(console.error);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputAccessCode(e.target.value);
  };

  let describedById: string | undefined;
  if (isError) {
    describedById = errorId;
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-14">
      <div className="flex flex-col gap-8 text-lg text-neutral-900">
        <p>Enter your access code to the product you have purchased.</p>
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1">
            <Label htmlFor={inputId}>{ACCESS_CODE_LABEL}</Label>
            <Popover
              side="right"
              align="start"
              trigger={
                <button
                  type="button"
                  aria-label={ACCESS_CODE_HELP_TRIGGER_LABEL}
                  className="inline-flex size-4 items-center justify-center rounded-full bg-brand-white text-neutral-1100"
                >
                  <InfoCircledIcon width={16} height={16} aria-hidden />
                </button>
              }
            >
              <div className="flex w-[320px] flex-col gap-6 pr-6 text-sm text-neutral-1100">
                <p>
                  If your school or district has purchased a digital product for
                  your classroom, please ask your administrator to provide the
                  access code.
                </p>
                <p>
                  If you are a Magazines+ Subscriber, you will receive the
                  access code via email, or find it in your Digital Resource
                  Guide. For magazine support, email{' '}
                  <a
                    href={`mailto:${MAGAZINES_SUPPORT_EMAIL}`}
                    className="text-blue-700 underline"
                  >
                    {MAGAZINES_SUPPORT_EMAIL}
                  </a>
                  .
                </p>
              </div>
            </Popover>
          </div>
          <input
            id={inputId}
            type="text"
            required
            // eslint-disable-next-line jsx-a11y/no-autofocus -- expected pattern for the primary input on dialog open
            autoFocus
            value={inputAccessCode}
            onChange={handleInputChange}
            aria-invalid={isError}
            aria-errormessage={isError ? errorId : undefined}
            aria-describedby={describedById}
            className={cn(
              'h-12 rounded-sm border border-neutral-400 bg-white p-3 pl-4 text-base font-normal leading-[40px] text-neutral-1100 focus-ring hover:enabled:border-neutral-1100 disabled:bg-neutral-100 disabled:text-neutral-700',
              {
                'border-2 border-red-700': isError,
              }
            )}
          />
          {isError && (
            <div role="alert" className="flex items-center gap-0.5">
              <ExclamationTriangleIcon className="text-red-700" aria-hidden />
              <p
                id={errorId}
                className="m-0 bg-inherit font-sans text-sm font-normal leading-normal text-red-700"
              >
                {accessCodeErrorMessage(errorCode)}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-4">
        <Button
          type="submit"
          variant="primary"
          disabled={!inputAccessCode.trim() || isRefetching}
        >
          Next
        </Button>
      </div>
    </form>
  );
}
